import React, { useState, useEffect } from 'react';
import './Hero.css';
import VoiceVisualizer from './VoiceVisualizer';
import { AgentStatus } from '../types';
import { geminiClient } from '../services/geminiService';

const Hero = ({ onOpenChatbot }) => {
  const [status, setStatus] = useState(AgentStatus.DISCONNECTED);
  const [amplitude, setAmplitude] = useState(0);

  useEffect(() => {
    // Set up callbacks
    geminiClient.onStatusChange = (newStatus) => {
      setStatus(newStatus);
    };

    geminiClient.onAudioData = (amp) => {
      setAmplitude(amp);
    };

    geminiClient.onLogMessage = (source, text) => {
      console.log(`[${source}]: ${text}`);
    };

    geminiClient.onToolCall = (name, args) => {
      console.log(`Tool called: ${name}`, args);
    };

    // Cleanup on unmount
    return () => {
      geminiClient.disconnect();
    };
  }, []);

  const handleConnect = async () => {
    if (status === AgentStatus.DISCONNECTED) {
      // Use 'company-info' as the use case ID to get company information system instruction
      await geminiClient.connect('company-info');
    } else {
      await geminiClient.disconnect();
      setStatus(AgentStatus.DISCONNECTED);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'disconnected': 'Ready to Connect',
      'connecting': 'Connecting...',
      'listening': 'Listening',
      'speaking': 'AI is Speaking',
      'thinking': 'Processing...',
      'error': 'Connection Error'
    };
    return labels[status] || status;
  };

  return (
    <section id="home" className="hero-new">
      <div className="hero-container">
        <div className="hero-content-new">
          <div className="hero-text-new">
            <div className="hero-badge">
              <i className="fas fa-star"></i>
              <span>AI-Powered Customer Service</span>
            </div>
            <h1>Automate Your Customer Service with <span className="gradient-text">AI Voice Agents & Chatbots</span></h1>
            <p>Stop missing calls and losing customers. Our AI solutions handle your customer interactions 24/7, so you can focus on growing your business.</p>
            
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Availability</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">&gt;50%</span>
                <span className="stat-label">Cost Savings</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">Quick</span>
                <span className="stat-label">Setup Time</span>
              </div>
            </div>
            
            <div className="hero-center-button">
              <a href="#contact" className="btn-secondary-new">
                <i className="fas fa-rocket"></i>
                Get Started Free
              </a>
            </div>
          </div>
          
          <div className="hero-visual-new">
            <div className="hero-voice-agent-container">
              {status === AgentStatus.DISCONNECTED ? (
                <button 
                  className="hero-play-button"
                  onClick={handleConnect}
                  aria-label="Talk to AI Assistant"
                >
                  <div className="play-icon-circle">
                    <i className="fas fa-play"></i>
                  </div>
                  <span className="play-button-text">Talk to Our AI Assistant</span>
                  <span className="play-button-subtext">Ask about our services, features, and how we can help</span>
                </button>
              ) : (
                <div className="hero-voice-active">
                  <div className="hero-voice-visualizer">
                    <VoiceVisualizer 
                      amplitude={amplitude} 
                      isActive={status !== AgentStatus.DISCONNECTED} 
                      status={status} 
                    />
                  </div>
                  <div className="hero-voice-controls">
                    <button
                      onClick={handleConnect}
                      className={`hero-voice-stop-button ${status === AgentStatus.DISCONNECTED ? 'play' : 'stop'}`}
                      disabled={status === 'connecting'}
                    >
                      {status === 'connecting' ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          <span>Connecting...</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-stop"></i>
                          <span>End Call</span>
                        </>
                      )}
                    </button>
                    <div className="hero-voice-status">
                      <div className={`status-dot status-${status}`}></div>
                      <span className="status-text">{getStatusLabel(status)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
